import { type Control, Controller, type FieldPath, type FieldValues } from 'react-hook-form'
import { Field, FieldDescription, FieldError, FieldLabel } from '@/shared/ui/field'
import { Input } from '@/shared/ui/input'
import { PasswordInput } from '@/shared/ui/password-input'

type StaffStepUpFieldsProps<TValues extends FieldValues> = {
  control: Control<TValues>
  /** Nombres de los campos en el formulario del llamador. */
  passwordName: FieldPath<TValues>
  codeName: FieldPath<TValues>
  needsSecondFactor: boolean
}

/**
 * Campos de confirmación de identidad reutilizables: contraseña y, cuando el backend
 * lo pide, el código del autenticador (TOTP). El código de recuperación es un fallback
 * de *login*, no del step-up dentro de la plataforma, así que acá no se ofrece. Es
 * agnóstico del dominio: sólo recibe el `control` de React Hook Form del formulario que
 * lo compone. Pareja de [[use-staff-step-up]].
 */
export function StaffStepUpFields<TValues extends FieldValues>({
  control,
  passwordName,
  codeName,
  needsSecondFactor
}: StaffStepUpFieldsProps<TValues>) {
  return (
    <>
      <Controller
        control={control}
        name={passwordName}
        rules={{ required: 'Ingresa tu contraseña.' }}
        render={({ field, fieldState }) => (
          <Field data-invalid={Boolean(fieldState.error)}>
            <FieldLabel htmlFor="staff-step-up-password">Contraseña</FieldLabel>
            <PasswordInput
              {...field}
              id="staff-step-up-password"
              autoComplete="current-password"
              className="normal-case"
            />
            <FieldDescription>
              Confirmamos tu identidad antes de cambiar datos sensibles de la cuenta.
            </FieldDescription>
            <FieldError errors={[fieldState.error]} />
          </Field>
        )}
      />

      {needsSecondFactor && (
        <Controller
          control={control}
          name={codeName}
          rules={{
            required: 'Ingresa el código de seis dígitos.',
            pattern: { value: /^\d{6}$/, message: 'El código debe tener seis dígitos.' }
          }}
          render={({ field, fieldState }) => (
            <Field data-invalid={Boolean(fieldState.error)}>
              <FieldLabel htmlFor="staff-step-up-code">
                Código de tu aplicación de autenticación
              </FieldLabel>
              <Input
                {...field}
                id="staff-step-up-code"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                className="font-mono tracking-wider"
              />
              <FieldError errors={[fieldState.error]} />
            </Field>
          )}
        />
      )}
    </>
  )
}
